import React from 'react';
import { RotateCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { RebuildGraphResponse } from '@/services/kbService';

export interface QARebuildResultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: RebuildGraphResponse | null;
}

export const QARebuildResultModal: React.FC<QARebuildResultModalProps> = ({
  open,
  onOpenChange,
  result,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCw className="h-5 w-5" />
            Graph Rebuild Results
          </DialogTitle>
        </DialogHeader>

        {result && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${result.success ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-destructive/10 border-destructive/30'}`}>
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${result.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.success ? 'Graph rebuild completed successfully' : 'Graph rebuild failed'}
                  </p>
                  <p className={`text-sm mt-1 ${result.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.message}
                  </p>
                </div>
              </div>
            </div>

            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Statistics</CardTitle>
              </CardHeader>
              <CardContent className="py-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Total in PostgreSQL</p>
                    <p className="text-lg font-bold text-primary">{result.total_in_postgres ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created in Neo4j</p>
                    <p className="text-lg font-bold text-emerald-400">{result.created_in_neo4j ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Deleted from Neo4j</p>
                    <p className="text-lg font-bold text-amber-500">{result.deleted_from_neo4j ?? 0}</p>
                  </div>
                  {(result.errors ?? 0) > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Errors</p>
                      <p className="text-lg font-bold text-red-400">{result.errors}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground">
              Duration: {result.duration_ms ? (result.duration_ms / 1000).toFixed(2) : 'N/A'} seconds
            </p>
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
